import { Brain, CalendarClock, Power, Trash2 } from 'lucide-react';
import type { ReactNode } from 'react';

import { Badge } from '@/components/ui/badge';

import type { CardEnvelope } from '@borradh-workspace/contracts/claire';

/**
 * What the memory tools DID, as cards.
 *
 * Saving, switching off, removing and listing a memory each used to end as a
 * sentence in the transcript. Claire rewords a memory as she stores it, so the
 * owner could not see what actually went in, where it applies, or when it
 * lapses — only her summary of it. These are result cards, not approvals:
 * `meta_remember` and `meta_setMemoryEnabled` are reversible and ungated,
 * and deleting already has its own `confirmation_required` card BEFORE the
 * delete. Visual language follows `MemoryRecallBadge` (the Brain icon, same
 * muted chrome) so everything memory-related reads as one family.
 */
export type MemorySavedPresentation = Extract<
  CardEnvelope,
  { type: 'memory_saved' }
>;
export type MemoryRemovedPresentation = Extract<
  CardEnvelope,
  { type: 'memory_removed' }
>;
export type MemoryUpdatedPresentation = Extract<
  CardEnvelope,
  { type: 'memory_updated' }
>;
export type MemoryListPresentation = Extract<
  CardEnvelope,
  { type: 'memory_list' }
>;

const scopeLabel = (scope: 'personal' | 'organization' | 'team') =>
  scope === 'personal' ? 'Personal' : 'Team';

const formatDate = (iso: string) => {
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime())
    ? iso
    : parsed.toLocaleDateString('en-IE', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
};

function MemoryCardShell({
  icon,
  title,
  children,
  testId,
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
  testId: string;
}) {
  return (
    <div
      data-testid={testId}
      className="w-full rounded-lg border bg-card p-4 sm:max-w-sm"
    >
      <div className="flex items-center gap-2 text-sm font-medium">
        {icon}
        {title}
      </div>
      {children}
    </div>
  );
}

function MemoryText({ children }: { children: ReactNode }) {
  return (
    <p className="mt-2 whitespace-pre-wrap break-words rounded-md bg-muted p-3 text-sm">
      {children}
    </p>
  );
}

function MemoryMeta({
  scope,
  appliesTo,
  expiresAt,
  extra,
}: {
  scope: 'personal' | 'organization' | 'team';
  appliesTo?: string;
  expiresAt?: string | null;
  extra?: ReactNode;
}) {
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      <Badge variant={scope === 'personal' ? 'secondary' : 'outline'}>
        {scopeLabel(scope)}
      </Badge>
      {appliesTo ? (
        <Badge variant="outline" className="font-normal">
          {appliesTo}
        </Badge>
      ) : null}
      {expiresAt ? (
        <Badge variant="secondary" className="gap-1 font-normal">
          <CalendarClock className="size-3" />
          Until {formatDate(expiresAt)}
        </Badge>
      ) : null}
      {extra}
    </div>
  );
}

export function MemorySavedCard({
  presentation,
}: {
  presentation: MemorySavedPresentation;
}) {
  return (
    <MemoryCardShell
      testId="claire-card-memory-saved"
      icon={<Brain className="size-4 text-muted-foreground" />}
      title="Saved to memory"
    >
      <MemoryText>{presentation.content}</MemoryText>
      <MemoryMeta
        scope={presentation.scope}
        appliesTo={presentation.appliesTo}
        expiresAt={presentation.expiresAt}
      />
    </MemoryCardShell>
  );
}

export function MemoryRemovedCard({
  presentation,
}: {
  presentation: MemoryRemovedPresentation;
}) {
  return (
    <MemoryCardShell
      testId="claire-card-memory-removed"
      icon={<Trash2 className="size-4 text-muted-foreground" />}
      title="Memory removed"
    >
      <MemoryText>{presentation.content}</MemoryText>
      <MemoryMeta
        scope={presentation.scope}
        appliesTo={presentation.appliesTo}
      />
    </MemoryCardShell>
  );
}

export function MemoryUpdatedCard({
  presentation,
}: {
  presentation: MemoryUpdatedPresentation;
}) {
  return (
    <MemoryCardShell
      testId="claire-card-memory-updated"
      icon={<Power className="size-4 text-muted-foreground" />}
      title={
        presentation.enabled ? 'Memory switched on' : 'Memory switched off'
      }
    >
      <MemoryText>{presentation.content}</MemoryText>
      <MemoryMeta
        scope={presentation.scope}
        appliesTo={presentation.appliesTo}
        extra={
          presentation.enabled ? null : (
            <Badge variant="destructive" className="font-normal">
              Not applied
            </Badge>
          )
        }
      />
    </MemoryCardShell>
  );
}

export function MemoryListCard({
  presentation,
}: {
  presentation: MemoryListPresentation;
}) {
  const { memories } = presentation;
  return (
    <MemoryCardShell
      testId="claire-card-memory-list"
      icon={<Brain className="size-4 text-muted-foreground" />}
      title={
        memories.length === 0
          ? 'Nothing saved yet'
          : `Saved memories (${memories.length})`
      }
    >
      {memories.length === 0 ? (
        <p className="mt-2 text-muted-foreground text-xs">
          Tell Claire "remember that…" and it lands here.
        </p>
      ) : (
        <ul className="mt-2 space-y-2">
          {memories.map((memory) => (
            <li
              key={memory.knowledgeEntryId}
              className={memory.enabled ? undefined : 'opacity-60'}
            >
              <p className="whitespace-pre-wrap break-words text-sm">
                {memory.content}
              </p>
              <MemoryMeta
                scope={memory.scope}
                appliesTo={memory.appliesTo}
                expiresAt={memory.expiresAt}
                extra={
                  memory.enabled ? null : (
                    <Badge variant="destructive" className="font-normal">
                      Switched off
                    </Badge>
                  )
                }
              />
            </li>
          ))}
        </ul>
      )}
    </MemoryCardShell>
  );
}
